import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getCredits, initBilling, subscribe } from '../api/endpoints';
import type { Credits } from '../types/api';
import { motion, AnimatePresence, useReducedMotion, type Variants, type Transition } from 'framer-motion';

const PAID_ENABLED = process.env.REACT_APP_PAID_PLANS === 'true';

type PlanDef = {
  key: 'free' | 'plus' | 'premium';
  name: string;
  blurb: string;
  limit: string;
  priceMonthly: string;
  priceYearly: string;
  features: string[];
  highlight?: boolean;
};

const PLANS: PlanDef[] = [
  {
    key: 'free',
    name: 'Free',
    blurb: 'Get started',
    limit: '100 credits / month',
    priceMonthly: '€0',
    priceYearly: '€0',
    features: [
      'Face analysis',
      'Basic recommendations',
      'Email support',
    ],
  },
  {
    key: 'plus',
    name: 'Plus',
    blurb: 'For regular use',
    limit: '1,300 credits / month',
    priceMonthly: '€9.99',
    priceYearly: '€83.88',
    features: [
      '1,300 monthly credits to spend across all features',
      'Hair Analysis — personalized hairstyle & color recommendations',
      'Makeup Analysis — personalized makeup palette & look recommendations',
      'Detailed condition breakdown',
      'Priority processing',
      'Product matching',
      'Export report (PDF)',
    ],
    highlight: true,
  },
  {
    key: 'premium',
    name: 'Premium',
    blurb: 'For power users',
    limit: '2,600 credits / month',
    priceMonthly: '€17.99',
    priceYearly: '€149.88',
    features: [
      'Everything in Plus (2,600 monthly credits)',
      'Hair Analysis — personalized hairstyle & color recommendations',
      'Makeup Analysis — personalized makeup palette & look recommendations',
      'Routine tracking',
      'Early feature access',
      'Priority support',
    ],
  },
];

export default function PlansPage(): React.JSX.Element {
  const [credits, setCredits] = useState<Credits | null>(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<'monthly' | 'yearly'>('monthly');
  const [subscribingKey, setSubscribingKey] = useState<string | null>(null);
  // EU Consumer Rights Directive: for paid, auto-renewing digital subscriptions
  // the user must explicitly request immediate access and acknowledge they waive
  // their 14-day right of withdrawal before we can start the service.
  const [euWaiver, setEuWaiver] = useState(false);

  const prefersReduced = useReducedMotion();
  const spring: Transition = prefersReduced
    ? { duration: 0 }
    : { type: 'spring', stiffness: 420, damping: 36, mass: 0.9 };
  const quick: Transition = prefersReduced ? { duration: 0 } : { duration: 0.18 };

  const pageIn: Variants = {
    hidden: { opacity: 0, y: 8 },
    visible: { opacity: 1, y: 0, transition: spring },
  };
  const listContainer: Variants = {
    hidden: {},
    visible: { transition: { staggerChildren: prefersReduced ? 0 : 0.06, delayChildren: prefersReduced ? 0 : 0.04 } },
  };
  const listItem: Variants = {
    hidden: { opacity: 0, y: 6 },
    visible: { opacity: 1, y: 0, transition: spring },
  };

  useEffect(() => {
    getCredits()
      .then((c) => { setCredits(c); })
      .catch(() => { setCredits(null); })
      .finally(() => { setLoading(false); });
  }, []);

  const currentPlanKey = useMemo(() => {
    const n = credits?.plan_name.toLowerCase() ?? '';
    if (n.includes('premium')) return 'premium';
    if (n.includes('plus')) return 'plus';
    return 'free';
  }, [credits]);

  const monthlyUsedPct = useMemo(() => {
    if (!credits?.monthly_grant) return 0;
    return Math.min(100, Math.round(((credits.monthly_grant - credits.monthly_balance) / credits.monthly_grant) * 100));
  }, [credits]);

  const handleSubscribe = async (planKey: string) => {
    // Paid, auto-renewing plans require the EU withdrawal-waiver acknowledgement
    // before we start billing / grant immediate access.
    if (planKey !== 'free' && !euWaiver) return;
    try {
      setSubscribingKey(planKey);
      const oldPlan = credits?.plan_name;
      await initBilling();
      await subscribe({ plan_key: planKey } satisfies Record<string, unknown>);
      const updated = await getCredits();
      setCredits(updated);
      const { postUserPlanChange } = await import('../api/telemetry');
      void postUserPlanChange(planKey, oldPlan).catch(() => { /* telemetry optional */ });
    } finally {
      setSubscribingKey(null);
    }
  };

  if (loading || !credits) {
    return (
      <div className="card card--bordered">
        <div
          className="skeleton"
          style={{ height: '16px', width: '128px', background: 'var(--c-charcoal-06)', borderRadius: 'var(--r-sm)', marginBottom: 'var(--s-4)' }}
        />
        <div
          className="skeleton"
          style={{ height: '8px', background: 'var(--c-charcoal-06)', borderRadius: 'var(--r-sm)' }}
        />
      </div>
    );
  }

  return (
    <motion.div
      variants={listContainer}
      initial="hidden"
      animate="visible"
      className="stack-6"
    >
      {/* Header: plan name + daily usage */}
      <motion.section variants={pageIn} className="card card--bordered">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--s-4)' }}>
          <div className="stack-2">
            <h1 className="t-h2" style={{ margin: 0 }}>
              Your <em className="t-italic t-teal">plan.</em>
            </h1>
            <div className="row-3">
              <span className="t-small t-mute">Current plan</span>
              <span className="pill">{credits.plan_name}</span>
            </div>
          </div>

          <div className="stack-2">
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--s-5)' }}>
              <span className="t-small t-mute">Monthly credits</span>
              <span className="t-small">{credits.monthly_balance} / {credits.monthly_grant}</span>
            </div>
            <div className="plan-usage-bar">
              <motion.div
                className="plan-usage-bar__fill"
                initial={{ width: 0 }}
                animate={{ width: `${monthlyUsedPct}%` }}
                transition={spring}
              />
            </div>
            <span className="t-small t-mute">+ {credits.surplus_balance} surplus credits</span>
          </div>
        </div>
      </motion.section>

      {/* Coming soon / contact us */}
      {!PAID_ENABLED ? (
        <motion.section variants={pageIn} className="card card--bordered">
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--s-6)', flexWrap: 'wrap' }}>
            <div className="stack-4" style={{ maxWidth: '56ch' }}>
              <span className="pill pill--teal" style={{ alignSelf: 'flex-start' }}>Coming soon</span>
              <div className="stack-3">
                <h2 className="t-h3" style={{ margin: 0 }}>
                  Paid plans are <em className="t-italic t-teal">coming.</em>
                </h2>
                <p className="t-body t-mute" style={{ margin: 0 }}>
                  We&apos;re still finalising subscriptions. If you need more than your current monthly
                  credit grant, you can <a href="/wallet" className="t-teal">buy credit packs</a> or write to us
                  and we&apos;ll top up your balance. Our team responds within a business day.
                </p>
              </div>

              <div className="row-3" style={{ flexWrap: 'wrap' }}>
                <a
                  href={`mailto:support@kivo.skin?subject=Add%20Credits&body=Hi%20Kivo%20team%2C%0A%0AI'd%20like%20to%20add%20credits%20to%20my%20account.%20My%20account%20email%20is%3A%20_____%0ARequested%20credits%3A%20_____%0AReason%3A%20_____%0A%0AThanks!`}
                  className="btn btn--primary"
                >
                  Email support@kivo.skin
                </a>
                <button
                  type="button"
                  onClick={() => { window.history.back(); }}
                  className="btn btn--ghost"
                >
                  Back
                </button>
              </div>

              <p className="t-small t-mute" style={{ margin: 0 }}>
                Current grant: <strong>{credits.monthly_grant} credits/month</strong> — we can top up your balance while billing is finalised.
              </p>
            </div>

            <div className="orb orb--sm" aria-hidden style={{ flexShrink: 0 }} />
          </div>
        </motion.section>
      ) : (
        <>
          {/* Billing period toggle */}
          <motion.section variants={pageIn} className="card card--bordered" style={{ padding: 'var(--s-4)' }}>
            <div className="billing-toggle">
              <motion.button
                type="button"
                whileTap={{ scale: prefersReduced ? 1 : 0.98 }}
                onClick={() => { setPeriod('monthly'); }}
                className={`billing-toggle__btn${period === 'monthly' ? ' is-active' : ''}`}
              >
                Monthly
              </motion.button>
              <motion.button
                type="button"
                whileTap={{ scale: prefersReduced ? 1 : 0.98 }}
                onClick={() => { setPeriod('yearly'); }}
                className={`billing-toggle__btn${period === 'yearly' ? ' is-active' : ''}`}
              >
                Yearly <span className="t-mute" style={{ marginLeft: 'var(--s-1)' }}>— save ~20%</span>
              </motion.button>
            </div>
          </motion.section>

          {/* Plan cards */}
          <motion.section
            variants={listContainer}
            initial="hidden"
            animate="visible"
            className="g-3"
          >
            {PLANS.map((plan) => {
              const isCurrent = currentPlanKey === plan.key;
              const isLoading = subscribingKey === plan.key;
              const price = period === 'monthly' ? plan.priceMonthly : plan.priceYearly;

              return (
                <motion.article
                  key={plan.key}
                  variants={listItem}
                  layout
                  transition={spring}
                  whileHover={{ y: prefersReduced ? 0 : -2 }}
                  whileTap={{ scale: prefersReduced ? 1 : 0.99 }}
                  className={`card card--bordered${plan.highlight ? ' card--highlight' : ''}`}
                  style={{ display: 'flex', flexDirection: 'column', position: 'relative' }}
                >
                  {plan.highlight && (
                    <motion.div
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={quick}
                      className="pill pill--teal"
                      style={{ position: 'absolute', top: 'var(--s-4)', right: 'var(--s-4)' }}
                    >
                      Most popular
                    </motion.div>
                  )}

                  <header className="stack-2" style={{ marginBottom: 'var(--s-4)' }}>
                    <span className="t-label">{plan.blurb}</span>
                    <h3 className="t-h3" style={{ margin: 0 }}>{plan.name}</h3>
                    <span className="t-small t-mute">{plan.limit}</span>
                  </header>

                  <div className="plan-price" style={{ marginBottom: 'var(--s-2)' }}>
                    <AnimatePresence mode="wait" initial={false}>
                      <motion.span
                        key={period}
                        className="plan-price__amount"
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        transition={quick}
                      >
                        {price}
                      </motion.span>
                    </AnimatePresence>
                    <span className="plan-price__period">
                      / {period === 'monthly' ? 'month' : 'year'}
                    </span>
                  </div>
                  {period === 'yearly' && plan.priceMonthly !== '€0' && (
                    <span className="plan-price__strike" style={{ marginBottom: 'var(--s-4)' }}>
                      {plan.priceMonthly} / mo
                    </span>
                  )}
                  {plan.key !== 'free' && (
                    <p className="t-label t-mute" style={{ margin: `0 0 var(--s-4)`, lineHeight: 1.5 }}>
                      {price} billed {period === 'monthly' ? 'monthly' : 'yearly'}, excl. VAT
                      (VAT added at checkout). Auto-renews every {period === 'monthly' ? 'month' : 'year'} until
                      cancelled — cancel anytime from your profile.
                    </p>
                  )}

                  <ul
                    className="stack-3"
                    style={{ listStyle: 'none', padding: 0, margin: `0 0 var(--s-5)` }}
                  >
                    {plan.features.map((f) => (
                      <li key={f} style={{ display: 'flex', gap: 'var(--s-2)', alignItems: 'flex-start' }}>
                        <span className="plan-check">
                          <CheckIcon />
                        </span>
                        <span className="t-small">{f}</span>
                      </li>
                    ))}
                  </ul>

                  <div style={{ marginTop: 'auto' }}>
                    {isCurrent ? (
                      <button
                        type="button"
                        disabled
                        className="btn btn--ghost btn--full"
                      >
                        Current plan
                      </button>
                    ) : (
                      <motion.button
                        type="button"
                        whileTap={{ scale: prefersReduced ? 1 : 0.98 }}
                        onClick={() => handleSubscribe(plan.key)}
                        disabled={isLoading || (plan.key !== 'free' && !euWaiver)}
                        className="btn btn--primary btn--full"
                        title={plan.key !== 'free' && !euWaiver ? 'Please confirm the acknowledgement below to continue' : undefined}
                      >
                        {isLoading ? 'Processing…' : `Choose ${plan.name}`}
                      </motion.button>
                    )}
                  </div>
                </motion.article>
              );
            })}
          </motion.section>

          {/* Checkout compliance: pricing terms, withdrawal-right waiver, policies */}
          <motion.section variants={pageIn} className="card card--bordered" style={{ padding: 'var(--s-5)' }}>
            <label
              htmlFor="eu-withdrawal-waiver"
              style={{ display: 'flex', gap: 'var(--s-3)', alignItems: 'flex-start', cursor: 'pointer' }}
            >
              <input
                id="eu-withdrawal-waiver"
                type="checkbox"
                checked={euWaiver}
                onChange={(e) => { setEuWaiver(e.target.checked); }}
                style={{ marginTop: '2px', width: 18, height: 18, accentColor: 'var(--c-kivo-teal)', flexShrink: 0 }}
              />
              <span className="t-small" style={{ lineHeight: 1.55 }}>
                I request that my subscription starts immediately and I acknowledge that,
                because the digital service begins right away, I lose my 14-day right of
                withdrawal once it starts (EU Consumer Rights Directive 2011/83/EU).
              </span>
            </label>

            <p className="t-label t-mute" style={{ margin: `var(--s-4) 0 var(--s-2)`, lineHeight: 1.6 }}>
              Subscriptions renew automatically for the selected period until you cancel.
              Prices shown exclude VAT; applicable VAT is calculated and added at checkout.
              You can cancel at any time from your profile; cancellation stops the next renewal
              and keeps access until the end of the paid period.
            </p>

            <nav
              aria-label="Legal policies"
              className="t-label"
              style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--s-2) var(--s-3)', color: 'var(--ink-muted)' }}
            >
              <Link to="/terms" className="hover:underline">Terms of Service</Link>
              <span aria-hidden>•</span>
              <Link to="/privacy" className="hover:underline">Privacy Policy</Link>
              <span aria-hidden>•</span>
              <Link to="/refund" className="hover:underline">Refund &amp; Cancellation</Link>
              <span aria-hidden>•</span>
              <Link to="/cookies" className="hover:underline">Cookie Policy</Link>
            </nav>
          </motion.section>
        </>
      )}

      <motion.p
        variants={listItem}
        className="t-small t-mute"
        style={{ textAlign: 'center', margin: 0 }}
      >
        Fair use guidelines apply.
      </motion.p>
    </motion.div>
  );
}

function CheckIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
